import React, { useCallback, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { adminClient } from '@/api/adminClient';
import { ORGANISATION_SIZES, SECTORS } from './customerProfile';
import DiscardChangesDialog from './DiscardChangesDialog';
import { useDirtyNavigationGuard } from './useDirtyNavigationGuard';

const CONTROL = 'mt-1 min-h-11 w-full rounded-lg border border-input bg-background px-3 py-2 text-foreground disabled:opacity-60';

export default function AdminCustomerNew() {
  const navigate = useNavigate();
  const submitting = useRef(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: '', sector: '', size: '' });
  const [error, setError] = useState('');
  const dirty = !!(form.name || form.sector || form.size);
  const clearDraft = useCallback(() => setForm({ name: '', sector: '', size: '' }), []);
  const navigationGuard = useDirtyNavigationGuard(dirty && !busy, clearDraft, busy);

  async function submit(event) {
    event.preventDefault();
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setError('');
    try {
      const row = await adminClient('/entities/Organisation', {
        method: 'POST',
        body: { ...form, name: form.name.trim(), current_dc_level: 0 },
      });
      navigationGuard.allowNextNavigation();
      navigate(`/admin/customers/${encodeURIComponent(row.id)}`, {
        replace: true,
        state: { created: true, inviteOwner: true },
      });
    } catch (requestError) {
      setError(requestError.message || 'Customer could not be created.');
      submitting.current = false;
      setBusy(false);
    }
  }

  return <div className="space-y-5">
    <Link to="/admin/customers" className="inline-flex min-h-11 items-center underline">← Customers</Link>
    <div><h1 className="text-2xl font-bold">New customer</h1><p className="mt-1 text-sm text-muted-foreground">Create the customer record, then invite its owner. Your provider organisation and membership stay unchanged.</p></div>
    {error && <p role="alert" className="rounded-lg border border-destructive/40 p-3 text-red-800">{error}</p>}
    <form onSubmit={submit} className="max-w-2xl space-y-4 rounded-xl border border-border bg-card p-4 sm:p-5">
      <label htmlFor="new-customer-name" className="block text-sm font-medium">Organisation name<input id="new-customer-name" required autoFocus disabled={busy} value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} className={CONTROL} /></label>
      <label htmlFor="new-customer-sector" className="block text-sm font-medium">Sector<select id="new-customer-sector" disabled={busy} value={form.sector} onChange={(event) => setForm((current) => ({ ...current, sector: event.target.value }))} className={CONTROL}><option value="">Not specified</option>{SECTORS.map((sector) => <option key={sector} value={sector}>{sector}</option>)}</select></label>
      <label htmlFor="new-customer-size" className="block text-sm font-medium">Organisation size<select id="new-customer-size" disabled={busy} value={form.size} onChange={(event) => setForm((current) => ({ ...current, size: event.target.value }))} className={CONTROL}><option value="">Not specified</option>{ORGANISATION_SIZES.map((size) => <option key={size.value} value={size.value}>{size.label}</option>)}</select></label>
      <button type="submit" disabled={busy || !form.name.trim()} className="min-h-11 rounded-lg bg-teal-700 px-4 font-semibold text-white disabled:opacity-50">{busy ? 'Creating…' : 'Create customer'}</button>
    </form>
    <DiscardChangesDialog open={!!navigationGuard.pending} subject="new customer" onCancel={navigationGuard.cancel} onConfirm={navigationGuard.confirm} />
  </div>;
}
